/**
 * Decorate client list reads with SPA SourceCache (SWR) without changing I*Client shapes.
 */

import { policyForSource, type ApiEnvelope, type SourceCacheSource } from '@zunelo/shared';
import { getDefaultSourceCache, type SourceCache } from './sourceCache';
import { sourceCacheStorageKey } from './types';
import type {
  IAgentsClient,
  IGmailClient,
  IGitHubClient,
  IJiraClient,
  ISlackClient,
  IUnattendedInboxClient,
  IWorkspaceClient,
} from '../clients/types';

export type ListClientUidResolver = () => string | undefined;

let resolveUidGlobal: ListClientUidResolver = () => undefined;
let activeForcedScopes = 0;

export function setSourceCacheUidResolver(resolver: ListClientUidResolver): void {
  resolveUidGlobal = resolver;
}

export function isForcedListReadsActive(): boolean {
  return activeForcedScopes > 0;
}

/** Force list getters to bypass soft-fresh hits (bootstrap / retry / refresh). */
export async function withForcedListReads<T>(fn: () => Promise<T>): Promise<T> {
  activeForcedScopes++;
  try {
    return await fn();
  } finally {
    activeForcedScopes = Math.max(0, activeForcedScopes - 1);
  }
}

function wrapListMethod<T>(
  cache: SourceCache,
  uid: ListClientUidResolver,
  source: SourceCacheSource,
  listKey: string,
  fetcher: () => Promise<ApiEnvelope<T>>,
  force?: boolean,
): Promise<ApiEnvelope<T>> {
  const key = sourceCacheStorageKey(uid(), source, listKey);
  const policy = policyForSource(source);
  const shouldForce = force === true || isForcedListReadsActive();
  const result = cache.getOrRevalidate({
    key,
    source,
    policy,
    fetcher,
    force: shouldForce,
  });
  if (result.fromCache && result.envelope && !shouldForce) {
    if (result.cache?.freshness === 'stale') {
      // Soft-TTL SWR: return stale immediately; refresh cache in the background.
      void result.revalidatePromise.catch(() => undefined);
    }
    return Promise.resolve(result.envelope);
  }
  return result.revalidatePromise;
}

function invalidateList(
  cache: SourceCache,
  uid: ListClientUidResolver,
  source: SourceCacheSource,
  listKey = 'list',
): void {
  cache.invalidate(sourceCacheStorageKey(uid(), source, listKey));
}

function afterInvalidate<TArgs extends unknown[], TResult>(
  invalidate: () => void,
  method: (...args: TArgs) => Promise<TResult>,
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs) => {
    const result = await method(...args);
    invalidate();
    return result;
  };
}

/**
 * Proxy decorator keeps `instanceof` and prototype methods intact
 * (object spread of class instances drops prototype methods).
 */
function decorateClient<T extends object>(base: T, overrides: Partial<T>): T {
  return new Proxy(base, {
    get(target, prop, receiver) {
      if (Object.prototype.hasOwnProperty.call(overrides, prop)) {
        return (overrides as Record<string | symbol, unknown>)[prop];
      }
      const value = Reflect.get(target, prop, receiver);
      if (typeof value === 'function') {
        return (value as (...args: unknown[]) => unknown).bind(target);
      }
      return value;
    },
  });
}

export interface WrapListClientOptions {
  cache?: SourceCache;
  resolveUid?: ListClientUidResolver;
  /** When true, list reads bypass soft TTL. */
  force?: boolean;
}

export function wrapJiraClient(
  client: IJiraClient,
  options: WrapListClientOptions = {},
): IJiraClient {
  const cache = options.cache ?? getDefaultSourceCache();
  const uid = options.resolveUid ?? (() => resolveUidGlobal());
  const force = options.force;
  return decorateClient(client, {
    getIssues: () => wrapListMethod(cache, uid, 'jira', 'list', () => client.getIssues(), force),
    updateWorkflowState: afterInvalidate(
      () => invalidateList(cache, uid, 'jira'),
      client.updateWorkflowState.bind(client),
    ),
    toggleCriterion: afterInvalidate(
      () => invalidateList(cache, uid, 'jira'),
      client.toggleCriterion.bind(client),
    ),
    addAcceptanceCriterion: afterInvalidate(
      () => invalidateList(cache, uid, 'jira'),
      client.addAcceptanceCriterion.bind(client),
    ),
    updateAcceptanceCriterion: afterInvalidate(
      () => invalidateList(cache, uid, 'jira'),
      client.updateAcceptanceCriterion.bind(client),
    ),
    deleteAcceptanceCriterion: afterInvalidate(
      () => invalidateList(cache, uid, 'jira'),
      client.deleteAcceptanceCriterion.bind(client),
    ),
    cycleSubtask: afterInvalidate(
      () => invalidateList(cache, uid, 'jira'),
      client.cycleSubtask.bind(client),
    ),
    generateAiSummary: afterInvalidate(
      () => invalidateList(cache, uid, 'jira'),
      client.generateAiSummary.bind(client),
    ),
  });
}

export function wrapGitHubClient(
  client: IGitHubClient,
  options: WrapListClientOptions = {},
): IGitHubClient {
  const cache = options.cache ?? getDefaultSourceCache();
  const uid = options.resolveUid ?? (() => resolveUidGlobal());
  const force = options.force;
  return decorateClient(client, {
    getPullRequests: () =>
      wrapListMethod(cache, uid, 'github', 'list', () => client.getPullRequests(), force),
    toggleCommentResolution: afterInvalidate(
      () => invalidateList(cache, uid, 'github'),
      client.toggleCommentResolution.bind(client),
    ),
    retryCheckRun: afterInvalidate(
      () => invalidateList(cache, uid, 'github'),
      client.retryCheckRun.bind(client),
    ),
    reRunAllChecks: afterInvalidate(
      () => invalidateList(cache, uid, 'github'),
      client.reRunAllChecks.bind(client),
    ),
    approvePullRequest: afterInvalidate(
      () => invalidateList(cache, uid, 'github'),
      client.approvePullRequest.bind(client),
    ),
    mergePullRequest: afterInvalidate(
      () => invalidateList(cache, uid, 'github'),
      client.mergePullRequest.bind(client),
    ),
    publishAiReview: afterInvalidate(
      () => invalidateList(cache, uid, 'github'),
      client.publishAiReview.bind(client),
    ),
  });
}

export function wrapSlackClient(
  client: ISlackClient,
  options: WrapListClientOptions = {},
): ISlackClient {
  const cache = options.cache ?? getDefaultSourceCache();
  const uid = options.resolveUid ?? (() => resolveUidGlobal());
  const force = options.force;
  return decorateClient(client, {
    getThreads: () => wrapListMethod(cache, uid, 'slack', 'list', () => client.getThreads(), force),
    postReply: afterInvalidate(
      () => invalidateList(cache, uid, 'slack'),
      client.postReply.bind(client),
    ),
  });
}

export function wrapUnattendedInboxClient(
  client: IUnattendedInboxClient,
  options: WrapListClientOptions = {},
): IUnattendedInboxClient {
  const cache = options.cache ?? getDefaultSourceCache();
  const uid = options.resolveUid ?? (() => resolveUidGlobal());
  const force = options.force;
  return decorateClient(client, {
    getUnattendedInbox: () =>
      wrapListMethod(
        cache,
        uid,
        'unattended-inbox',
        'list',
        () => client.getUnattendedInbox({ fresh: force === true || isForcedListReadsActive() }),
        force,
      ),
  });
}

export function wrapAgentsClient(
  client: IAgentsClient,
  options: WrapListClientOptions = {},
): IAgentsClient {
  const cache = options.cache ?? getDefaultSourceCache();
  const uid = options.resolveUid ?? (() => resolveUidGlobal());
  const force = options.force;
  return decorateClient(client, {
    getSessions: () =>
      wrapListMethod(cache, uid, 'agents', 'list', () => client.getSessions(), force),
    toggleAgentRunning: afterInvalidate(
      () => invalidateList(cache, uid, 'agents'),
      client.toggleAgentRunning.bind(client),
    ),
    launchAgentTask: afterInvalidate(
      () => invalidateList(cache, uid, 'agents'),
      client.launchAgentTask.bind(client),
    ),
  });
}

export function wrapGmailClient(
  client: IGmailClient,
  options: WrapListClientOptions = {},
): IGmailClient {
  const cache = options.cache ?? getDefaultSourceCache();
  const uid = options.resolveUid ?? (() => resolveUidGlobal());
  const force = options.force;
  return decorateClient(client, {
    getMessages: async () => {
      return wrapListMethod(cache, uid, 'gmail', 'list', () => client.getMessages(), force);
    },
    markRead: afterInvalidate(
      () => invalidateList(cache, uid, 'gmail'),
      client.markRead.bind(client),
    ),
    replyToMessage: afterInvalidate(
      () => invalidateList(cache, uid, 'gmail'),
      client.replyToMessage.bind(client),
    ),
  });
}

export function wrapWorkspaceClient(
  client: IWorkspaceClient,
  options: WrapListClientOptions = {},
): IWorkspaceClient {
  const cache = options.cache ?? getDefaultSourceCache();
  const uid = options.resolveUid ?? (() => resolveUidGlobal());
  const force = options.force;
  const inv = (listKey: string) => () => {
    invalidateList(cache, uid, 'workspace', listKey);
    invalidateList(cache, uid, 'workspace', 'bootstrap');
  };

  return decorateClient(client, {
    getWorkspaceBundle: () =>
      wrapListMethod(
        cache,
        uid,
        'workspace',
        'bootstrap',
        () => client.getWorkspaceBundle(),
        force,
      ),
    getReminders: () =>
      wrapListMethod(cache, uid, 'workspace', 'reminders', () => client.getReminders(), force),
    getNotes: () =>
      wrapListMethod(cache, uid, 'workspace', 'notes', () => client.getNotes(), force),
    getLlmWiki: () =>
      wrapListMethod(cache, uid, 'workspace', 'llmWiki', () => client.getLlmWiki(), force),
    getDailyGroups: () =>
      wrapListMethod(cache, uid, 'workspace', 'dailyGroups', () => client.getDailyGroups(), force),
    getHoldData: () =>
      wrapListMethod(cache, uid, 'workspace', 'holdData', () => client.getHoldData(), force),
    getSuggestions: () =>
      wrapListMethod(cache, uid, 'workspace', 'suggestions', () => client.getSuggestions(), force),
    getExternalSignals: () =>
      wrapListMethod(
        cache,
        uid,
        'workspace',
        'externalSignals',
        () => client.getExternalSignals(),
        force,
      ),
    getPrTalkMap: () =>
      wrapListMethod(cache, uid, 'workspace', 'prTalkMap', () => client.getPrTalkMap(), force),
    saveReminder: afterInvalidate(inv('reminders'), client.saveReminder.bind(client)),
    toggleReminder: afterInvalidate(inv('reminders'), client.toggleReminder.bind(client)),
    deleteReminder: afterInvalidate(inv('reminders'), client.deleteReminder.bind(client)),
    saveNote: afterInvalidate(inv('notes'), client.saveNote.bind(client)),
    deleteNote: afterInvalidate(inv('notes'), client.deleteNote.bind(client)),
    saveLlmWikiPage: afterInvalidate(inv('llmWiki'), client.saveLlmWikiPage.bind(client)),
    deleteLlmWikiPage: afterInvalidate(inv('llmWiki'), client.deleteLlmWikiPage.bind(client)),
    saveDailyGroup: afterInvalidate(inv('dailyGroups'), client.saveDailyGroup.bind(client)),
    sendDaily: afterInvalidate(inv('dailyGroups'), client.sendDaily.bind(client)),
  });
}
